import React from "react";
import {
    Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Flex, Button, Link, IconButton, useToast
} from "@chakra-ui/react";
import { FiClock, FiUsers, FiTarget, FiCalendar, FiMapPin, FiAward, FiMail, FiHelpCircle, FiVideo, FiCopy } from "react-icons/fi";
import { FaCalendarPlus, FaWhatsapp } from "react-icons/fa";
import moment from "moment";
import { formatTime12h, getGoogleMapsSearchUrl, getOrdinalSuffix } from "../utils/formatHelpers";
import { getJoinButtonState, openMeetingLink, copyMeetingLink } from "../utils/meetingHelpers";
import { COLORS } from "../styles/colors";
import { sectionCardProps } from "./portalUi";

const getSessionBadge = (badgeText) => {
    if (badgeText === "Completed") return { bg: "green.500", color: "white" };
    if (badgeText === "Ongoing") return { bg: "blue.500", color: "white" };
    return { bg: "gray.200", color: "gray.700" };
};

export const CourseOverviewTab = ({
    courseMaterials,
    handleDownloadSessionCalendar,
    handleDownloadAllSessionsCalendar,
    mailtoUrl,
    whatsappUrl,
}) => {
    const toast = useToast();

    const handleCopyMeetingLink = async (url) => {
        const ok = await copyMeetingLink(url);
        toast({
            title: ok ? "Link copied" : "Could not copy link",
            description: ok ? "Meeting link copied to clipboard." : "Please copy the link manually.",
            status: ok ? "success" : "error",
            duration: 2500,
            isClosable: true,
        });
    };

    return (
        <VStack align="stretch" spacing={5}>
            {/* Overview Stats */}
            {(courseMaterials?.course_type?.course_duration || courseMaterials?.max_capacity || courseMaterials?.attendance_req) && (
                <Box {...sectionCardProps}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                        {courseMaterials?.course_type?.course_duration && (
                            <HStack
                                spacing={3}
                                p={3}
                                bg="white"
                                borderRadius="xl"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Icon as={FiClock} color={COLORS.shadowBlue} boxSize={5} />
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Duration</Text>
                                    <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>
                                        {courseMaterials.course_type.course_duration} hours
                                    </Text>
                                </Box>
                            </HStack>
                        )}
                        {courseMaterials?.max_capacity && (
                            <HStack
                                spacing={3}
                                p={3}
                                bg="white"
                                borderRadius="xl"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Icon as={FiUsers} color={COLORS.shadowBlue} boxSize={5} />
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Capacity</Text>
                                    <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>
                                        {courseMaterials.max_capacity} students
                                    </Text>
                                </Box>
                            </HStack>
                        )}
                        {courseMaterials?.attendance_req && (
                            <HStack
                                spacing={3}
                                p={3}
                                bg="white"
                                borderRadius="xl"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Icon as={FiTarget} color={COLORS.mandarin} boxSize={5} />
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Attendance Required</Text>
                                    <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>
                                        {courseMaterials.attendance_req}%
                                    </Text>
                                </Box>
                            </HStack>
                        )}
                    </SimpleGrid>
                </Box>
            )}

            {/* Schedule Information */}
            <Box {...sectionCardProps}>
                <VStack align="stretch" spacing={4}>
                    <HStack spacing={2} color="gray.600" flexWrap="wrap">
                        <Icon as={FiCalendar} boxSize={5} flexShrink={0} color={COLORS.shadowBlue} />
                        <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue} flexShrink={0}>
                            Date Range
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            {moment(courseMaterials?.start_date).format("DD MMM YYYY")} – {moment(courseMaterials?.end_date).format("DD MMM YYYY")}
                        </Text>
                    </HStack>

                    {courseMaterials?.course_dates && Array.isArray(courseMaterials.course_dates) && courseMaterials.course_dates.length > 0 && (
                        <Box>
                            <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
                                <HStack spacing={2}>
                                    <Icon as={FiCalendar} boxSize={5} color={COLORS.shadowBlue} />
                                    <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>
                                        Class Schedule
                                    </Text>
                                </HStack>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    borderRadius="full"
                                    borderColor="gray.200"
                                    color={COLORS.maastrichtBlue}
                                    leftIcon={<Icon as={FaCalendarPlus} boxSize={4} />}
                                    onClick={handleDownloadAllSessionsCalendar}
                                    fontSize="xs"
                                    fontWeight="600"
                                    _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                                >
                                    Add All to Calendar
                                </Button>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                {courseMaterials.course_dates.map((schedule, idx) => {
                                    const scheduleDate = moment(schedule.date);
                                    const today = moment().startOf("day");
                                    const daysDifference = scheduleDate.diff(today, "days");
                                    const isPast = daysDifference < 0;
                                    const isToday = daysDifference === 0;
                                    const joinState = getJoinButtonState(schedule);
                                    const isOnline = !!schedule.is_online || !!joinState.meetingUrl;

                                    // Relative timing always sits beside the date (same as physical sessions)
                                    let badgeText = "";
                                    if (joinState.status === "ended" || isPast) badgeText = "Ended";
                                    else if (isToday) badgeText = "Ongoing";
                                    else badgeText = `In ${daysDifference} ${daysDifference === 1 ? "day" : "days"}`;

                                    const badgeColors = getSessionBadge(
                                        badgeText === "Ended" ? "Completed" : (badgeText === "Ongoing" ? "Ongoing" : badgeText)
                                    );

                                    return (
                                        <Box
                                            key={idx}
                                            p={{ base: 3, md: 4 }}
                                            bg="white"
                                            borderRadius="xl"
                                            border="1px solid"
                                            borderColor="gray.200"
                                        >
                                            <VStack align="start" spacing={1.5} w="100%">
                                                <HStack justify="space-between" w="100%">
                                                    <HStack spacing={2} flex="1" minW={0} flexWrap="wrap">
                                                        <Text fontSize="sm" fontWeight="700" color={COLORS.maastrichtBlue} whiteSpace="nowrap">
                                                            {moment(schedule.date).format("ddd, DD MMM YYYY")}
                                                        </Text>
                                                        <Badge
                                                            bg={badgeColors.bg}
                                                            color={badgeColors.color}
                                                            fontSize="2xs"
                                                            borderRadius="full"
                                                            textTransform="none"
                                                            px={2.5}
                                                            py={0.5}
                                                            flexShrink={0}
                                                        >
                                                            {badgeText}
                                                        </Badge>
                                                    </HStack>
                                                    <IconButton
                                                        icon={<Icon as={FaCalendarPlus} boxSize={4} />}
                                                        size="sm"
                                                        variant="ghost"
                                                        color={COLORS.shadowBlue}
                                                        aria-label="Add to Calendar"
                                                        title="Add to Calendar"
                                                        borderRadius="full"
                                                        flexShrink={0}
                                                        onClick={() => handleDownloadSessionCalendar(
                                                            schedule,
                                                            courseMaterials?.course_type?.name || "Module",
                                                            courseMaterials.course_dates
                                                        )}
                                                    />
                                                </HStack>
                                                {schedule.start_time && schedule.end_time && (
                                                    <Text fontSize="xs" color="gray.600">
                                                        {formatTime12h(schedule.start_time)} – {formatTime12h(schedule.end_time)}
                                                    </Text>
                                                )}
                                                {schedule.room && (
                                                    <Text fontSize="xs" color="gray.600">
                                                        {isOnline ? "Meeting software" : "Room"}: {schedule.room}
                                                    </Text>
                                                )}
                                                {joinState.meetingUrl && (
                                                    <HStack spacing={2} pt={1} flexWrap="wrap">
                                                        {joinState.status !== "ended" && (
                                                            <Button
                                                                size="xs"
                                                                colorScheme="green"
                                                                variant="solid"
                                                                borderRadius="full"
                                                                leftIcon={<Icon as={FiVideo} />}
                                                                onClick={() => openMeetingLink(joinState.meetingUrl)}
                                                            >
                                                                Join Now
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            borderRadius="full"
                                                            leftIcon={<Icon as={FiCopy} />}
                                                            onClick={() => handleCopyMeetingLink(joinState.meetingUrl)}
                                                        >
                                                            Copy link
                                                        </Button>
                                                    </HStack>
                                                )}
                                            </VStack>
                                        </Box>
                                    );
                                })}
                            </SimpleGrid>
                        </Box>
                    )}
                </VStack>
            </Box>

            {/* Venue and Trainer Information */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {(courseMaterials?.venue || courseMaterials?.venues) && (
                    <Box {...sectionCardProps}>
                        <HStack mb={3} spacing={2}>
                            <Icon as={FiMapPin} color={COLORS.shinyShamrock} boxSize={5} />
                            <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>Venue Details</Text>
                        </HStack>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="600" color={COLORS.maastrichtBlue}>
                                {courseMaterials?.venues?.name || courseMaterials?.venue}
                            </Text>
                            {(courseMaterials?.course_dates || []).some((d) => d.is_online || d.meeting_url) && (
                                <Text fontSize="xs" color="gray.600">
                                    Some sessions are online — use Join Now / Copy link in the schedule above.
                                </Text>
                            )}
                            {(courseMaterials?.venues?.address || courseMaterials?.venues?.postal_code) &&
                             courseMaterials?.venues?.address !== "Online" &&
                             (() => {
                                const mapsUrl = getGoogleMapsSearchUrl(courseMaterials.venues?.address, courseMaterials.venues?.postal_code);
                                const addressLine = courseMaterials.venues?.address || "";
                                const postalLine = courseMaterials.venues?.postal_code ? `Singapore ${courseMaterials.venues.postal_code}` : "";
                                const fullDisplay = [addressLine, postalLine].filter(Boolean).join(", ");
                                if (!fullDisplay) return null;
                                return mapsUrl ? (
                                    <Link
                                        href={mapsUrl}
                                        isExternal
                                        fontSize="xs"
                                        color="blue.600"
                                        fontWeight="500"
                                        _hover={{ textDecoration: "underline" }}
                                        display="inline-flex"
                                        alignItems="flex-start"
                                        gap={1}
                                        flexDirection="column"
                                    >
                                        <span>{addressLine}</span>
                                        {postalLine && <span>{postalLine}</span>}
                                    </Link>
                                ) : (
                                    <Text fontSize="xs" color="gray.600">{fullDisplay}</Text>
                                );
                            })()}
                            {courseMaterials?.rooms && (() => {
                                const formatFloorUnit = () => {
                                    if (!courseMaterials.rooms.floor && !courseMaterials.rooms.unit) return "";
                                    const parts = [];
                                    if (courseMaterials.rooms.floor) {
                                        const floor = courseMaterials.rooms.floor;
                                        const floorText = isNaN(floor) ? floor : `${floor}${getOrdinalSuffix(Number(floor))} Floor`;
                                        parts.push(floorText);
                                    }
                                    if (courseMaterials.rooms.unit) parts.push(`Unit ${courseMaterials.rooms.unit}`);
                                    return parts.length > 0 ? ` (${parts.join(", ")})` : "";
                                };
                                return (
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                        {courseMaterials.rooms.name}{formatFloorUnit()}
                                        {courseMaterials.rooms.capacity && ` - Capacity: ${courseMaterials.rooms.capacity}`}
                                    </Text>
                                );
                            })()}
                        </VStack>
                    </Box>
                )}

                {courseMaterials?.trainers && (
                    <Box {...sectionCardProps}>
                        <HStack mb={3} spacing={2}>
                            <Icon as={FiUsers} color={COLORS.shadowBlue} boxSize={5} />
                            <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>Trainer</Text>
                        </HStack>
                        <Text fontSize="sm" fontWeight="600" color={COLORS.maastrichtBlue}>
                            {courseMaterials.trainers.name}
                        </Text>
                    </Box>
                )}
            </SimpleGrid>

            {/* Need help? */}
            {(mailtoUrl || whatsappUrl) && (
                <Box {...sectionCardProps}>
                    <HStack mb={3} spacing={2}>
                        <Icon as={FiHelpCircle} color={COLORS.mandarin} boxSize={5} />
                        <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>Need help?</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Have a question about this course? Reach out to your trainer or venue contact.
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                        {mailtoUrl && (
                            <Button
                                as="a"
                                href={mailtoUrl}
                                size="sm"
                                borderRadius="full"
                                leftIcon={<Icon as={FiMail} boxSize={4} />}
                                variant="outline"
                                borderColor="gray.200"
                                color={COLORS.maastrichtBlue}
                                fontWeight="600"
                                _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                            >
                                Email
                            </Button>
                        )}
                        {whatsappUrl && (
                            <Button
                                as="a"
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="sm"
                                borderRadius="full"
                                leftIcon={<Icon as={FaWhatsapp} boxSize={4} />}
                                bg={COLORS.shinyShamrock}
                                color="white"
                                fontWeight="600"
                                _hover={{ opacity: 0.9 }}
                            >
                                WhatsApp
                            </Button>
                        )}
                    </HStack>
                </Box>
            )}

            {/* Course Details */}
            {(courseMaterials?.course_type?.course_objectives || courseMaterials?.course_type?.prerequisites || courseMaterials?.course_type?.minimum_entry_requirements) && (
                <Box {...sectionCardProps}>
                    <Heading fontSize="md" mb={4} color={COLORS.maastrichtBlue} fontWeight="700">
                        <HStack spacing={2}>
                            <Icon as={FiTarget} boxSize={5} color={COLORS.shadowBlue} />
                            <Text as="span">Course Details</Text>
                        </HStack>
                    </Heading>
                    {courseMaterials?.course_type?.course_objectives && (
                        <Box mb={4}>
                            <Text fontWeight="600" mb={2} fontSize="sm" color={COLORS.maastrichtBlue}>Course Objectives</Text>
                            <Text fontSize="sm" color="gray.600" pl={4} borderLeft="3px solid" borderColor="blue.200">
                                {courseMaterials.course_type.course_objectives}
                            </Text>
                        </Box>
                    )}
                    {courseMaterials?.course_type?.prerequisites && (
                        <Box mb={4}>
                            <Text fontWeight="600" mb={2} fontSize="sm" color={COLORS.maastrichtBlue}>Prerequisites</Text>
                            <Text fontSize="sm" color="gray.600" pl={4} borderLeft="3px solid" borderColor="orange.200">
                                {courseMaterials.course_type.prerequisites}
                            </Text>
                        </Box>
                    )}
                    {courseMaterials?.course_type?.minimum_entry_requirements && (
                        <Box mb={0}>
                            <Text fontWeight="600" mb={2} fontSize="sm" color={COLORS.maastrichtBlue}>Entry Requirements</Text>
                            <Text fontSize="sm" color="gray.600" pl={4} borderLeft="3px solid" borderColor="green.200">
                                {courseMaterials.course_type.minimum_entry_requirements}
                            </Text>
                        </Box>
                    )}
                </Box>
            )}

            {/* Certification Info */}
            {courseMaterials?.course_type?.certification && (
                <Box {...sectionCardProps}>
                    <HStack mb={2} spacing={2}>
                        <Icon as={FiAward} color={COLORS.mandarin} boxSize={5} />
                        <Text fontWeight="600" fontSize="sm" color={COLORS.maastrichtBlue}>Certification</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                        {courseMaterials.course_type.certification}
                    </Text>
                </Box>
            )}
        </VStack>
    );
};
